import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { sportsApi } from '@/services/sportsApi';
import type {
  SportsEvent,
  SportsEventVariants,
  SportsPreflightKind,
  SportsProviderName,
  SportsStreamVariant,
} from '@/types/sports';

interface Props {
  event: SportsEvent;
  activeSource: SportsProviderName | null;
  /** The specific variant within the active provider, when one is pinned. */
  activeSourceId?: string | null;
  activeStreamNo?: number | null;
  /** Provider-level selection (Auto variant for that provider, or Auto overall). */
  onSelect(source: SportsProviderName | null): void;
  /** Pin a specific language/quality variant within a provider. */
  onSelectVariant(source: SportsProviderName, sourceId: string, streamNo?: number): void;
}

type ProbeStatus =
  | { kind: 'idle' }
  | { kind: 'probing' }
  | { kind: 'ok' }
  | { kind: 'fail'; reason: SportsPreflightKind | 'unknown'; message?: string };

type VariantsStatus = 'idle' | 'loading' | 'ready' | 'error';

const SOURCE_LABELS: Record<SportsProviderName, string> = {
  streamed: 'streamed.pk',
  ppv: 'ppv.st',
  sportsrc: 'sportsrc.org',
  daddylive: 'daddylive',
  bintvdrm: 'bintv (DRM)',
  roxie: 'RoxieStreams',
};

/** A variant is the active one iff its provider + sourceId + streamNo all match. */
function isActiveVariant(
  v: SportsStreamVariant,
  activeSource: SportsProviderName | null,
  activeSourceId: string | null | undefined,
  activeStreamNo: number | null | undefined,
): boolean {
  if (v.provider !== activeSource) return false;
  if (v.sourceId !== activeSourceId) return false;
  return (v.streamNo ?? null) === (activeStreamNo ?? null);
}

/**
 * Sports analogue of SourceMenu — renders one row per provider present in
 * event.sources[], plus an Auto row. Each provider row expands into a
 * sub-dropdown of its selectable language/quality streams, fetched lazily from
 * GET /api/sports-stream/:id/sources when the menu first opens. Per-row
 * preflight probe surfaces status without forcing a player switch.
 */
export function SportsSourceMenu({
  event,
  activeSource,
  activeSourceId,
  activeStreamNo,
  onSelect,
  onSelectVariant,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [variants, setVariants] = useState<SportsEventVariants | null>(null);
  const [variantsStatus, setVariantsStatus] = useState<VariantsStatus>('idle');
  const [expanded, setExpanded] = useState<Set<SportsProviderName>>(new Set());
  const [probes, setProbes] = useState<Record<SportsProviderName, ProbeStatus>>({
    streamed: { kind: 'idle' },
    ppv: { kind: 'idle' },
    sportsrc: { kind: 'idle' },
    daddylive: { kind: 'idle' },
    bintvdrm: { kind: 'idle' },
    roxie: { kind: 'idle' },
  });

  const presentSources = event.sources.map((s) => s.provider);
  // Stable, predictable provider ordering.
  const uniqueSources = Array.from(new Set(presentSources)) as SportsProviderName[];

  function variantsFor(source: SportsProviderName): SportsStreamVariant[] {
    return variants?.providers.find((p) => p.provider === source)?.variants ?? [];
  }

  async function loadVariants(): Promise<void> {
    setVariantsStatus('loading');
    try {
      const result = await sportsApi.listSources(event.id);
      setVariants(result);
      setVariantsStatus('ready');
    } catch {
      // Best-effort: the provider rows still work; only the sub-lists are lost.
      setVariantsStatus('error');
    }
  }

  function openMenu(): void {
    const next = !open;
    setOpen(next);
    if (next) {
      if (variantsStatus === 'idle') void loadVariants();
      // Auto-expand the active provider so its language options are visible.
      if (activeSource) setExpanded(new Set([activeSource]));
    }
  }

  function toggleExpanded(source: SportsProviderName): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
    if (variantsStatus === 'idle') void loadVariants();
  }

  async function probe(source: SportsProviderName): Promise<void> {
    setProbes((prev) => ({ ...prev, [source]: { kind: 'probing' } }));
    try {
      const result = await sportsApi.preflight(event.id, source);
      setProbes((prev) => ({
        ...prev,
        [source]: result.ok
          ? { kind: 'ok' }
          : { kind: 'fail', reason: result.kind ?? 'unknown', message: result.message },
      }));
    } catch (err) {
      setProbes((prev) => ({
        ...prev,
        [source]: { kind: 'fail', reason: 'unknown', message: err instanceof Error ? err.message : 'probe failed' }
      }));
    }
  }

  return (
    <div className="source-menu sports-source-menu">
      <button
        type="button"
        className="btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={openMenu}
      >
        {t('source.trigger', { name: activeSource ? SOURCE_LABELS[activeSource] : t('source.auto') })}
      </button>
      {open && (
        <ul role="menu" className="source-menu__list">
          <li role="menuitemradio" aria-checked={activeSource === null}>
            <button
              type="button"
              className={`source-menu__row${activeSource === null ? ' is-active' : ''}`}
              onClick={() => { onSelect(null); setOpen(false); }}
            >
              {t('source.auto')}
            </button>
          </li>
          {uniqueSources.map((source) => {
            const probeStatus = probes[source];
            const isExpanded = expanded.has(source);
            // Provider row is "active" when the provider is selected AND no
            // specific variant is pinned (the provider-level Auto variant).
            const providerRowActive = activeSource === source && !activeSourceId;
            const list = variantsFor(source);
            return (
              <li key={source} role="menuitemradio" aria-checked={activeSource === source}>
                <div className="source-menu__rowline">
                  <button
                    type="button"
                    className="source-menu__expander"
                    aria-label={t('sportsSource.streamsToggle', { name: SOURCE_LABELS[source] })}
                    aria-expanded={isExpanded}
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(source); }}
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>
                  <button
                    type="button"
                    className={`source-menu__row${providerRowActive ? ' is-active' : ''}`}
                    onClick={() => { onSelect(source); setOpen(false); }}
                  >
                    <span>{SOURCE_LABELS[source]}</span>
                    <ProbeBadge status={probeStatus} t={t} />
                  </button>
                  <button
                    type="button"
                    className="source-menu__probe"
                    aria-label={t('sportsSource.probeAria', { name: SOURCE_LABELS[source] })}
                    onClick={(e) => { e.stopPropagation(); void probe(source); }}
                    disabled={probeStatus.kind === 'probing'}
                  >
                    {t('sportsSource.test')}
                  </button>
                </div>
                {isExpanded && (
                  <ul className="source-menu__sublist" role="group">
                    {variantsStatus === 'loading' && (
                      <li className="source-menu__subnote">{t('sportsSource.loadingStreams')}</li>
                    )}
                    {variantsStatus !== 'loading' && list.length === 0 && (
                      <li className="source-menu__subnote">{t('sportsSource.noStreams')}</li>
                    )}
                    {list.map((v) => {
                      const active = isActiveVariant(v, activeSource, activeSourceId, activeStreamNo);
                      return (
                        <li
                          key={`${v.sourceId}#${v.streamNo ?? 0}`}
                          role="menuitemradio"
                          aria-checked={active}
                        >
                          <button
                            type="button"
                            className={`source-menu__subrow${active ? ' is-active' : ''}`}
                            onClick={() => { onSelectVariant(source, v.sourceId, v.streamNo); setOpen(false); }}
                          >
                            {v.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ProbeBadge({ status, t }: { status: ProbeStatus; t: TFunction }): JSX.Element | null {
  if (status.kind === 'idle') return null;
  if (status.kind === 'probing') return <span className="source-menu__badge">…</span>;
  if (status.kind === 'ok') return <span className="source-menu__badge source-menu__badge--ok" aria-label={t('sportsSource.probeOk')}>✓</span>;
  return (
    <span
      className="source-menu__badge source-menu__badge--fail"
      aria-label={t('sportsSource.probeFailed', { reason: status.reason })}
      title={status.message ?? status.reason}
    >
      ✗
    </span>
  );
}
